import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Receipt as ReceiptIcon } from "lucide-react";
import { MixedUseCorrectionRow } from "@/components/vat/MixedUseCorrectionRow";
import { toast } from "sonner";
import { EstvFiguresTable } from "@/components/EstvFiguresTable";

const PERIODS = [
  { value: "Q1", label: "Q1 (Jan–Mär)" },
  { value: "Q2", label: "Q2 (Apr–Jun)" },
  { value: "Q3", label: "Q3 (Jul–Sep)" },
  { value: "Q4", label: "Q4 (Okt–Dez)" },
  { value: "H1", label: "1. Semester" },
  { value: "H2", label: "2. Semester" },
  { value: "FULL", label: "Ganzes Jahr" },
  ...["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"].map((l, i) => ({ value: `M${String(i + 1).padStart(2, "0")}`, label: `Monat: ${l}` })),
];

const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function VatStatementWidget({ companyId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState("Q1");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/vat/statement`, { params: { year, period } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "MWST-Abrechnung konnte nicht erstellt werden.");
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-4 border border-slate-200 bg-white" data-testid="vat-statement-widget">
      <div className="flex items-start gap-2 mb-3">
        <ReceiptIcon className="h-4 w-4 text-amber-700" />
        <div>
          <div className="text-sm font-medium text-slate-900">MWST-Abrechnung</div>
          <div className="text-[11px] text-slate-500">Effektive Methode oder Saldosteuersatz · Monat · Quartal · Semester · Jahr</div>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div className="space-y-1">
          <Label className="text-[11px]">Jahr</Label>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="w-24 text-sm" data-testid="vat-year-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">Periode</Label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} className="text-sm rounded-md border border-slate-200 px-2 py-1.5" data-testid="vat-period-select">
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <Button type="button" size="sm" className="rounded-full bg-slate-900 text-slate-50" onClick={load} disabled={loading} data-testid="vat-load-button">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Berechnen"}
        </Button>
        {data && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full"
            onClick={async () => {
              try {
                const r = await apiClient.get(
                  `/companies/${companyId}/vat/statement/emwst-xml`,
                  { params: { year, period }, responseType: "blob" },
                );
                const blob = new Blob([r.data], { type: "application/xml" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `emwst_${year}_${period}.xml`;
                a.click();
                URL.revokeObjectURL(url);
              } catch (e) {
                /* noop */
              }
            }}
            data-testid="vat-emwst-xml-button"
            title="eCH-0217 eMWST-XML herunterladen (ESTV ePortal)"
          >
            ESTV-XML
          </Button>
        )}
      </div>

      {data && (
        <div className="space-y-2" data-testid="vat-statement-result">
          <div className="text-[11px] text-slate-500">
            Periode: <strong>{data.period_label}</strong> ({data.period_from} → {data.period_to}) · Methode: <strong>{data.method === "saldo" ? "Saldosteuersatz" : "Effektiv"}</strong>
            {" · "}Abrechnungsart: <strong data-testid="vat-accounting-basis-label">{data.accounting_basis === "received" ? "vereinnahmtes Entgelt (nach Zahlung)" : "vereinbartes Entgelt (nach Rechnung)"}</strong>
          </div>
          {data.accounting_basis === "received" && (
            <div className="rounded border border-sky-200 bg-sky-50 p-2 text-[11px] text-sky-900" data-testid="vat-deferred-block">
              Noch nicht fällig (offene Posten): Umsatzsteuer auf offenen Debitoren <span className="font-mono font-semibold">{fmt(data.deferred_vat_receivables)}</span>
              {" · "}Vorsteuer auf offenen Kreditoren <span className="font-mono font-semibold">{fmt(data.deferred_vat_payables)}</span>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="rounded bg-slate-50 p-2">
              <div className="text-[10px] text-slate-500">Umsatz steuerpflichtig (netto)</div>
              <div className="font-mono text-sm">{fmt(data.revenue_subject_to_vat)}</div>
            </div>
            <div className="rounded bg-slate-50 p-2">
              <div className="text-[10px] text-slate-500">Umsatz steuerbefreit</div>
              <div className="font-mono text-sm">{fmt(data.revenue_exempt)}</div>
            </div>
            <div className="rounded bg-emerald-50 p-2">
              <div className="text-[10px] text-emerald-700">Umsatzsteuer (Output)</div>
              <div className="font-mono text-sm">{fmt(data.vat_on_revenue)}</div>
            </div>
            <div className="rounded bg-rose-50 p-2">
              <div className="text-[10px] text-rose-700">Vorsteuer (Input)</div>
              <div className="font-mono text-sm">{fmt(data.vat_on_expenses)}</div>
            </div>
            <div className={`rounded p-2 ${data.vat_due >= 0 ? "bg-amber-50" : "bg-emerald-50"} col-span-2 md:col-span-1`}>
              <div className="text-[10px] text-slate-700">Zahllast / Guthaben</div>
              <div className="font-mono text-base font-semibold" data-testid="vat-due-value">{fmt(data.vat_due)}</div>
            </div>
          </div>

          {data.reverse_charge && (data.reverse_charge.count > 0 || data.reverse_charge.year_status?.level === "warning" || data.reverse_charge.year_status?.level === "exceeded") && (
            <div className="rounded border border-indigo-200 bg-indigo-50/60 p-2 mt-2 text-[11px] text-indigo-900" data-testid="vat-reverse-charge-block">
              <div className="font-semibold">Bezugsteuer (Dienstleistungen aus dem Ausland) · {data.reverse_charge.count} Beleg(e)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
                <div><div className="text-[10px] text-indigo-700">Ziffer 382 · Bemessung</div><div className="font-mono">{fmt(data.reverse_charge.net_chf)}</div></div>
                <div><div className="text-[10px] text-indigo-700">Ziffer 382 · Steuer 8.1 %</div><div className="font-mono" data-testid="vat-rc-tax">{fmt(data.reverse_charge.tax_chf)}</div></div>
                {data.reverse_charge.deductible ? (
                  <>
                    <div><div className="text-[10px] text-indigo-700">Vorsteuer Ziffer 400</div><div className="font-mono">{fmt(data.reverse_charge.input_400)}</div></div>
                    <div><div className="text-[10px] text-indigo-700">Vorsteuer Ziffer 405</div><div className="font-mono">{fmt(data.reverse_charge.input_405)}</div></div>
                  </>
                ) : (
                  <div className="col-span-2"><div className="text-[10px] text-indigo-700">Vorsteuerabzug</div><div>keiner ({data.method === "saldo" ? "Saldosteuersatz" : "nicht registriert"}) – Bezugsteuer ist Aufwand</div></div>
                )}
              </div>
              {data.reverse_charge.year_status?.hint && (
                <div className={`mt-1 rounded px-2 py-1 ${data.reverse_charge.year_status.level === "exceeded" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`} data-testid="vat-rc-threshold-hint">
                  {data.reverse_charge.year_status.hint}
                </div>
              )}
            </div>
          )}

          <EstvFiguresTable companyId={companyId} year={year} period={period} figures={data.figures} onChanged={load} />
          <MixedUseCorrectionRow companyId={companyId} year={year} period={period} mixedUse={data.figures?.mixed_use} onChanged={load} />

          {data.method === "saldo" && data.vat_due_saldo != null && (
            <div className="rounded border-2 border-violet-300 bg-violet-50 p-2 mt-2" data-testid="vat-saldo-block">
              <div className="text-[11px] text-violet-800">
                Saldosteuersatz <strong>{data.saldo_rate}%</strong> auf Brutto-Umsatz <span className="font-mono">{fmt(data.gross_revenue)}</span>
              </div>
              <div className="font-mono text-base font-semibold text-violet-900">Zahllast (Saldo): {fmt(data.vat_due_saldo)}</div>
            </div>
          )}

          {data.breakdown_by_rate?.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold text-slate-700 mb-1">Aufschlüsselung nach Satz</div>
              <table className="w-full text-[11px]">
                <thead className="text-slate-600">
                  <tr><th className="text-left">Satz</th><th className="text-right">Umsatz netto</th><th className="text-right">USt</th><th className="text-right">Aufwand netto</th><th className="text-right">VSt</th></tr>
                </thead>
                <tbody>
                  {data.breakdown_by_rate.map(b => (
                    <tr key={b.rate} className="border-t border-slate-100">
                      <td className="py-1 font-medium">{b.rate}%</td>
                      <td className="text-right font-mono">{fmt(b.revenue_net)}</td>
                      <td className="text-right font-mono">{fmt(b.vat_revenue)}</td>
                      <td className="text-right font-mono">{fmt(b.expense_net)}</td>
                      <td className="text-right font-mono">{fmt(b.vat_expense)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
